import React, { useEffect } from 'react'
import '@fs/react-scripts/polyfills'
import { usePermission } from '@fs/zion-permissions'
import Root from '@fs/zion-root'
import { useTranslation } from 'react-i18next'
import { useExperiment } from '@fs/zion-experiments'
import { AppLayout, useOverlay } from '@fs/zion-ui'
import { useLocation } from '@fs/zion-router'
import { useDarkModeContext } from '../providers/darkModeProvider/DarkModeProvider'
import App from '../../App'
import { getIndexExploreContextUrl } from '../utils/utils'
import useAlertBanner from '../hooks/useAlertBanner'
import useFullscreenState from '../hooks/useFullscreenState'
import useImagePermission from '../hooks/useImagePermission'
import shouldShowLimitedAccessBanner from '../utils/limitedAccessBanner'
import LimitedAccessImageDialog from '../components/limitedAccessImageDialog/LimitedAccessImageDialog'

// Store for dynamic headers that can be updated at runtime
export const dynamicHeaders = {
  rmsendpointauthex: undefined,
}

const RootBase = () => {
  const [t] = useTranslation()
  const { theme } = useDarkModeContext()
  const location = useLocation()
  const contextUrl = getIndexExploreContextUrl()
  const [rmsEndpointAuthEx] = useExperiment('rmsEndpointAuthEx')
  const imageArk = location.pathname.split('/').pop()
  // imageErrorState is non-null when the image can't be shown (404 = it doesn't exist here).
  const { imagePermission, imageErrorState } = useImagePermission({
    imageArk,
    // only make this call for R2 or Full Text views.
    enabled: location.pathname.includes('/ark:'),
  })
  const [inDiscoveryCenter] = usePermission('ThemisPrmDiscoveryCenter', 'CrxCtxPublicAccess')
  const [inAffiliateLibrary] = usePermission('ThemisPrmAffiliate', 'CrxCtxPublicAccess')
  const permissionsDialogOverlay = useOverlay()

  const [isFullScreen] = useFullscreenState()

  useEffect(() => {
    if (rmsEndpointAuthEx) {
      dynamicHeaders.rmsendpointauthex = 'true'
    } else {
      delete dynamicHeaders.rmsendpointauthex
    }
  }, [rmsEndpointAuthEx])

  const permissionBannerProps = useAlertBanner({
    message: t('index.alertBanner.permission.message', 'You are viewing a limited access record.'),
    // dismissText: t('index.1931.Census.alert.dismiss-button', 'Dismiss'),
    type: 'info',
    confirmText: t('index.alertBanner.permission.learnMore', 'Learn More'),
    onConfirm: permissionsDialogOverlay.handleClick,
  })

  const showBanner = shouldShowLimitedAccessBanner({ imagePermission, inAffiliateLibrary, inDiscoveryCenter })
  const alertBannerProps = showBanner ? permissionBannerProps : null

  return (
    <Root
      analytics
      theme={theme}
      alertBannerProps={showBanner && alertBannerProps}
      footer={{ hidden: true }}
      header={{
        hidden: Boolean(contextUrl) || isFullScreen,
      }}
      fullScreen
    >
      <AppLayout fullWidth noScroll>
        <App imagePermission={imagePermission} imageErrorState={imageErrorState} />
      </AppLayout>
      <LimitedAccessImageDialog overlay={permissionsDialogOverlay} permissions={imagePermission} />
    </Root>
  )
}

export default RootBase
